/**
 * ORR-294 T6 — textos del reporte interno del ciclo de cobranza.
 *
 * Aparte del componente por el mismo motivo que `avisoCobranza.copy.ts`: exportar valores
 * desde el archivo de una plantilla rompe fast-refresh, y el copy conviene junto.
 */

/** Los motivos que devuelve `cobranza_universo.motivo_no_notificable`, en castellano. */
export const MOTIVOS_SIN_AVISO: Record<string, string> = {
  sin_email: 'Sin correo cargado',
  opt_out: 'Pidió no recibir avisos',
  suppression: 'Correo dado de baja por rebotes',
  desconocido: 'Motivo no identificado',
}

export const ETIQUETA_ETAPA: Record<string, string> = {
  periodo: 'Aviso del período',
  recordatorio: 'Recordatorio de cuota vencida',
  // El criterio (tres períodos, o cuatro con saldo chico) va en la descripción, no acá: la
  // etiqueta y su explicación quedan una debajo de la otra y repetirlo se lee como un error.
  escalamiento: 'Escalamiento',
  arranque: 'Aviso de arranque',
}

/**
 * ORR-343 — qué significa cada grupo, en las palabras del estudio.
 *
 * El reporte mostraba "Aviso del período: 34" y nada más. Lisandro (02/09) pidió la redacción
 * que le mandé por WhatsApp: el número solo no dice si esos 34 recibieron un reclamo o un aviso,
 * y esa diferencia es la que él necesita para saber si tiene que salir a hablar con alguien.
 *
 * Una etapa sin entrada acá sale con su número pelado, como antes: la descripción es un agregado,
 * nunca un requisito para que el reporte se arme.
 */
export const DESCRIPCION_ETAPA: Record<string, string> = {
  periodo:
    'Se les cargó la cuota del mes. Es el aviso de que ya está disponible, no un reclamo.',
  recordatorio: 'La cuota venció y sigue impaga. El mail no menciona la baja.',
  escalamiento:
    'Arrastran tres períodos o más. Es el único aviso que menciona la suspensión del servicio.',
  arranque:
    'Arrastran tres períodos o más, pero el mail sale con el texto de arranque: detalla la deuda sin mencionar la baja.',
}

/**
 * Los que quedaron fuera del envío, agrupados por motivo y de mayor a menor.
 *
 * Lisandro pidió ver la cantidad sin correo cargado JUNTO a los avisos, no solo en la tabla de
 * abajo. Va desglosado por motivo y no como un número único porque "no tiene mail" y "pidió no
 * recibir avisos" se resuelven distinto: uno es un dato que falta cargar, el otro es una decisión
 * del cliente. Medido en PROD el 02/09: 9 sin correo y 2 con opt-out conviven hoy.
 */
export function omitidosPorMotivo(
  omitidos: Array<{ motivo: string }>,
): Array<{ motivo: string; cantidad: number }> {
  const conteo = new Map<string, number>()
  for (const o of omitidos) conteo.set(o.motivo, (conteo.get(o.motivo) ?? 0) + 1)
  return [...conteo.entries()]
    .map(([motivo, cantidad]) => ({ motivo, cantidad }))
    .sort((a, b) => b.cantidad - a.cantidad)
}

export function asuntoReporteCobranza(input: {
  fecha: string
  dryRun: boolean
  total: number
  omitidos: number
}): string {
  const prefijo = input.dryRun ? '[Simulación] ' : ''
  const sinCobertura = input.omitidos > 0 ? `, ${input.omitidos} sin cobertura` : ''
  return `${prefijo}Cobranza ${input.fecha}: ${input.total} avisos${sinCobertura}`
}
